import { createSlice } from '@reduxjs/toolkit';

/**
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {{ id: string, isTarget: boolean, displayName: string }[]} structList
 * @typedef {{
 *    proteinFamily: string,
 *    targetList: structList,
 *    scoringTableState: {
 *        columnVisibilityModel: import('@mui/x-data-grid').GridColumnVisibilityModel
 *        scoring: Boolean,
 *        sidePanelWidth: number,
 *        activeChart: string,
 *        chartState: object,
 *    }
 *    compoundTableState: {
 *        columnVisibilityModel: import('@mui/x-data-grid').GridColumnVisibilityModel
 *        sidePanelWidth: number,
 *        activeChart: string,
 *        chartState: object,
 *    }
 * }} initialState
 */

const initialTableState = {
    scoringTable: {
        scoring: false,
        sidePanelWidth: 600,
        activeChart: 'bmapsTable',
        chartState: {
            coralMap: {
                activeCompounds: [],
                colorScheme: null,
                property: null,
                propertyThresholds: {},
                propertyPreferences: {},
            },
        },
    },
    compoundTable: {
        sidePanelWidth: 600,
        activeChart: 'bmapsTable',
    },
};

/** @type {initialState} */
export const initialState = {
    proteinFamily: '',
    targetList: [],
    tableState: initialTableState,
};

const slice = createSlice({
    name: 'projectState',
    initialState,
    reducers: {
        addStructure(state, action) {
            // ensure that target id is unique.
            if (state.targetList.some((value) => value.mapCase === action.payload.mapCase)) {
                return ({ ...state });
            }
            return {
                ...state,
                targetList: [
                    ...state.targetList,
                    action.payload,
                ],
            };
        },
        changeTarget(state, action) {
            const newState = state.targetList.map((value) => {
                if (action.payload.mapCase === value.mapCase) {
                    return ({
                        ...value,
                        isTarget: !value.isTarget,
                    });
                } else {
                    return (value);
                }
            });
            return {
                ...state,
                targetList: [...newState],
            };
        },
        setTargetInfo(state, action) {
            const newState = state.targetList.map((value) => {
                if (action.payload.mapCase === value.mapCase) {
                    return (action.payload);
                } else {
                    return (value);
                }
            });
            return {
                ...state,
                targetList: [...newState],
            };
        },
        removeStructure(state, action) {
            const newState = state.targetList.filter(
                (value) => value.mapCase !== action.payload.mapCase
            );
            return {
                ...state,
                targetList: [...newState],
            };
        },
        changeProteinFamily(state, action) {
            return {
                ...state,
                proteinFamily: action.payload,
            };
        },
        setTableState(state, action) {
            state.tableState = { ...state.tableState, ...action.payload };
        },
        setCompoundTableState(state, action) {
            state.tableState.compoundTable = {
                ...state.tableState.compoundTable,
                ...action.payload,
            };
        },
        setScoringTableState(state, action) {
            state.tableState.scoringTable = {
                ...state.tableState.scoringTable,
                ...action.payload,
            };
        },
        setCoralMapState(state, action) {
            state.tableState[action.payload.table].chartState.coralMap = {
                ...state.tableState[action.payload.table].chartState.coralMap,
                ...action.payload.coralMap,
            };
        },
        setActiveChart(state, action) {
            state.tableState[action.payload.table].activeChart = action.payload.chart;
        },
        setSidePanelWidth(state, action) {
            state.tableState[action.payload.table].sidePanelWidth = action.payload.sidePanelWidth;
        },
        removeAllStructures(state) {
            return {
                ...state,
                targetList: [],
            };
        },
        reset() {
            return initialState;
        },
    },
});

export default slice;
